import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/Dashboard/TopBar'; 

import ReservationTableRows from './ReservationTableRows';
import AddEditModal from './AddEditModal';
import DeleteModal from './DeleteModal';
import ViewModal from './ViewModal';
import { styles, getStatusStyle } from './ReservationStyles';

const ReservationTable = () => {
  // --- Sidebar State ---
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // --- State Management ---
  const [reservations, setReservations] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [statusCounts, setStatusCounts] = useState({ all: 0, Booked: 0, Reserved: 0, Cancelled: 0, New: 0 });
  const reservationsPerPage = 7; 

  // --- Modal States ---
  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModal] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  
  // --- Data States ---
  const [currentReservation, setCurrentReservation] = useState(null);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', date: '', time: '', persons: 1, status: 'New'
  });

  // --- Data Fetching ---
  const fetchReservations = async () => {
    try {
      const response = await axios.get('https://admin.theporch.in/api/cafe_reservations');
      const formattedData = response.data.map(item => ({
        id: item.id,
        name: item.name,
        email: item.email,
        phone: item.phone,
        date: item.date,
        time: item.time,
        persons: item.persons,
        status: item.status,
        submittedAt: item.submitted_at || item.created_at,
      })).sort((a, b) => b.id - a.id);

      setReservations(formattedData);
      const counts = formattedData.reduce((acc, r) => {
        if (acc.hasOwnProperty(r.status)) acc[r.status] += 1;
        acc.all += 1;
        return acc;
      }, { all: 0, Booked: 0, Reserved: 0, Cancelled: 0, New: 0 });
      setStatusCounts(counts);
    } catch (error) {
      console.error('Error fetching cafe bookings:', error);
    }
  };

  useEffect(() => { fetchReservations(); }, []);

  // --- Search & Filter Logic ---
  const displayReservations = useMemo(() => {
    return reservations.filter(r => {
      const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
      const matchesSearch = !searchTerm || 
        [r.name, r.phone, r.email].some(field => 
          String(field).toLowerCase().includes(searchTerm.toLowerCase())
        );
      return matchesStatus && matchesSearch;
    });
  }, [reservations, filterStatus, searchTerm]);

  // --- Pagination Logic ---
  const totalPages = Math.ceil(displayReservations.length / reservationsPerPage);
  const currentReservations = displayReservations.slice((page - 1) * reservationsPerPage, page * reservationsPerPage);

  // --- Handlers ---
  const handleAddReservationClick = () => {
    setCurrentReservation(null);
    setFormData({ name: '', email: '', phone: '', date: '', time: '', persons: 1, status: 'New' });
    setAddEditModalOpen(true);
  };
  const handleEditClick = (res) => { setCurrentReservation(res); setFormData({ ...res }); setAddEditModalOpen(true); };
  const handleDeleteClick = (id) => { setReservationToDelete(id); setDeleteConfirmModal(true); };
  const handleViewClick = (res) => { setCurrentReservation(res); setViewModalOpen(true); };
  const handleModalClose = () => { setAddEditModalOpen(false); setDeleteConfirmModal(false); setViewModalOpen(false); };

  const handleAddEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (currentReservation) await axios.put(`https://admin.theporch.in/api/cafe_reservations/${currentReservation.id}`, payload);
      else await axios.post('https://admin.theporch.in/api/cafe_reservations', payload);
      handleModalClose(); fetchReservations();
    } catch (error) { console.error('Save failed:', error); }
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`https://admin.theporch.in/api/cafe_reservations/${reservationToDelete}`);
      setDeleteConfirmModal(false); fetchReservations();
    } catch (error) { console.error('Delete failed:', error); }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden', backgroundColor: '#f3f4f6', position: 'relative' }}>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      
      <style>{`
        .sidebar-container { width: 260px; height: 100vh; flex-shrink: 0; background: #fff; transition: transform 0.3s ease; z-index: 1000; }
        .main-content-layout { flex-grow: 1; height: 100vh; overflow-y: auto; display: flex; flex-direction: column; width: calc(100% - 260px); transition: all 0.3s ease; }

        /* Attractive Pagination */
        .pagination-nav { display: flex; gap: 8px; align-items: center; }
        .pagination-btn {
          background: white; border: 1px solid #d1d5db; padding: 6px 14px; border-radius: 8px;
          cursor: pointer; font-weight: 600; font-size: 14px; transition: all 0.2s;
          display: flex; align-items: center; gap: 5px; color: #374151;
        }
        .pagination-btn:hover:not(:disabled) { background: #f9fafb; border-color: #9ca3af; }
        .pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        @media (max-width: 1024px) {
          .sidebar-container { position: fixed; left: 0; top: 0; transform: translateX(-100%); }
          .sidebar-container.open { transform: translateX(0); }
          .main-content-layout { width: 100%; }
          .sidebar-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 999; }
          .sidebar-overlay.active { display: block; }
        }

        /* COMPACT MOBILE TABLE (CARD VIEW) */
        @media (max-width: 768px) {
          .responsive-table thead { display: none; }
          .responsive-table tr { 
            display: grid; grid-template-columns: 1fr 1fr;
            margin-bottom: 12px; border: 1px solid #e5e7eb; 
            border-radius: 12px; padding: 10px; background: #fff;
            box-shadow: 0 2px 4px rgba(0,0,0,0.02);
          }
          .responsive-table td { 
            display: flex; flex-direction: column; 
            width: auto; padding: 5px !important; border: none !important;
            font-size: 13px; text-transform: none !important; /* Ensure content isn't forced uppercase */
          }
          .responsive-table td::before { 
            content: attr(data-label); font-weight: 700; 
            color: #9ca3af; font-size: 10px; text-transform: capitalize; margin-bottom: 2px;
          }
          .responsive-table td[data-label="Name"],
          .responsive-table td[data-label="Actions"],
          .responsive-table td[data-label="Email"] { grid-column: span 2; border-top: 1px solid #f3f4f6 !important; margin-top: 4px; padding-top: 8px !important; }
          
          .top-controls-flex { flex-direction: column; gap: 10px; }
          .filter-scroll { overflow-x: auto; white-space: nowrap; width: 100%; padding-bottom: 5px; }
        }
      `}</style>

      <div className={`sidebar-overlay ${isSidebarOpen ? 'active' : ''}`} onClick={toggleSidebar} />

      <div className={`sidebar-container ${isSidebarOpen ? 'open' : ''}`}>
        <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      </div>

      <main className="main-content-layout">
        <TopBar onMenuClick={toggleSidebar} isSidebarOpen={isSidebarOpen} />

        <div style={{ ...styles.tableContainer, padding: 'clamp(15px, 4vw, 24px)', flex: 1 }}>
          <h2 style={{ ...styles.headerTitle, marginBottom: '20px', fontSize: '1.25rem' }}>Cafe Reservations</h2>

          {/* Controls Bar */}
          <div className="top-controls-flex" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div className="filter-scroll" style={{ display: 'flex', gap: '5px' }}>
              {['all', 'Booked', 'Reserved', 'New', 'Cancelled'].map(status => (
                <button
                  key={status}
                  style={{ 
                    ...styles.filterButton, 
                    ...(filterStatus === status ? styles.filterButtonActive : styles.filterButtonInactive), 
                    padding: '6px 12px', 
                    fontSize: '13px',
                    textTransform: 'capitalize' // First letter uppercase only
                  }}
                  onClick={() => { setFilterStatus(status); setPage(1); }}
                >
                  {status} ({statusCounts[status] || 0})
                </button>
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input 
                type="text" placeholder="Search..." 
                style={{ ...styles.search, width: '180px', margin: 0, padding: '8px 12px' }} 
                value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }} 
              />
              <button style={{ ...styles.modalSaveBtn, margin: 0, padding: '8px 16px' }} onClick={handleAddReservationClick}>+ Add</button>
            </div>
          </div>

          <div style={{ ...styles.tableWrapper, background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <table className="responsive-table" style={styles.table}>
              <thead>
                <tr>
                  {/* Headers updated to First letter uppercase only */}
                  {['Id', 'Name', 'Email', 'Phone', 'Date', 'Time', 'Persons', 'Status', 'Submitted', 'Actions'].map(h => (
                    <th key={h} style={{...styles.tableHeader, padding: '12px 15px', textTransform: 'capitalize'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <ReservationTableRows 
                    data={currentReservations} onView={handleViewClick} 
                    onEdit={handleEditClick} onDelete={handleDeleteClick} 
                    getStatusStyle={getStatusStyle} 
                />
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>
              Showing <b>{currentReservations.length}</b> of <b>{displayReservations.length}</b>
            </span>
            <div className="pagination-nav">
              <button className="pagination-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <i className="fas fa-chevron-left"></i> Prev
              </button>
              <div style={{ padding: '0 10px', fontWeight: '700', color: '#374151', fontSize: '14px' }}>
                {page} / {totalPages || 1}
              </div>
              <button className="pagination-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

        {addEditModalOpen && <AddEditModal formData={formData} setFormData={setFormData} onClose={handleModalClose} onSubmit={handleAddEditSubmit} isEdit={!!currentReservation} />}
        {deleteConfirmModalOpen && <DeleteModal onClose={handleModalClose} onConfirm={handleDeleteConfirm} />}
        {viewModalOpen && <ViewModal reservation={currentReservation} onClose={handleModalClose} />}
      </main>
    </div>
  );
};

export default ReservationTable;